import { HttpEventType, HttpResponse } from "@angular/common/http";
import { Component, ElementRef, Inject, OnInit, ViewChild } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog } from "@angular/material/dialog";
import { MatTableDataSource } from "@angular/material/table";
import { NotifierService } from "angular-notifier";
import { timeout } from "rxjs/operators";
import { TestArea } from "src/app/common/test-area";
import { ApiService } from "src/app/services/api.service";
import { FilesService } from "src/app/services/files.service";
import { ErrorHelper } from "../../common/ErrorHelper";
import { User } from "../../model/user";
import { AppComponent } from "../../app.component";

export interface GlobalConfig {
  fileName: string;
  content: string;
}

const REGEXP_FILE_NAME = new RegExp("^(oneLayer_redirect_[A-Z0-9]{4}).*.xml$");

@Component({
  selector: "app-manage-one-layer-lookup-tables",
  templateUrl: "./manage-one-layer-lookup-tables.component.html",
  styleUrls: ["./manage-one-layer-lookup-tables.component.css"],
})
export class ManageOneLayerLookupTablesComponent implements OnInit {
  @ViewChild("fileInput", { static: false }) fileInput: ElementRef;

  selectedFiles: FileList;
  validatedFiles: File[];
  progressInfos = [];
  private _listFilter: string = "";
  filteredFiles: GlobalConfig[] = [];

  testArea = TestArea;
  selectedClass = "embargo";
  selectedTestArea = TestArea.EMBARGO;

  displayedColumns: string[] = ["fileName", "showFileContent"];
  dataSource: MatTableDataSource<GlobalConfig>;

  constructor(
    private apiService: ApiService,
    private filesService: FilesService,
    private notifierService: NotifierService,
    public dialog: MatDialog,
    public app: AppComponent
  ) {
    this.app.getUserData().then(
      function (user: User): void {
        if (user.hasEmbargoPermissions()) {
          this.selectTestArea(TestArea.EMBARGO);
        } else {
          this.selectTestArea(TestArea.PREPROD);
        }
      }.bind(this)
    );
  }

  get listFilter(): string {
    return this._listFilter;
  }
  set listFilter(value: string) {
    this._listFilter = value;
    this.filteredFiles = this.dataSource.data.filter((file: GlobalConfig) =>
      file.fileName.toLowerCase().includes(this._listFilter.toLowerCase())
    );
  }

  ngOnInit(): void {
    this.dataSource = new MatTableDataSource();
  }

  selectTestArea(test: TestArea): void {
    this.selectedTestArea = test;
    this.selectedClass = this.selectedTestArea
      .toLowerCase()
      .split(" ")
      .find((e) => true);
    this.loadGlobalConfigsAsync();
  }

  async loadGlobalConfigsAsync(): Promise<any> {
    try {
      await this.loadGlobalConfigs();
    } catch (error) {
      console.log(error);
    }
  }

  loadGlobalConfigs(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      return this.apiService
        .getOneLayerLookupTableConfigFiles(this.selectedTestArea)
        .toPromise()
        .then(
          (data) => {
            const configs = data;
            this.dataSource = new MatTableDataSource(configs);
            this.filteredFiles = this.dataSource.data;
            resolve(true);
          },
          (error) => {
            const errorMessage = ErrorHelper.disectError("load global configs", error);
            this.notifierService.notify("error", errorMessage);
            resolve(false);
          }
        );
    });
  }

  showFileContent(content: string): void {
    this.dialog.open(DialogDataFilesContentComponent, {
      data: {
        text: content,
      },
      width: "90%",
      maxWidth: "100vw",
    });
  }

  onClick(): void {
    this.fileInput.nativeElement.click();
  }

  selectFiles(event): void {
    this.progressInfos = [];
    this.validatedFiles = [];

    this.selectedFiles = event.target.files;
    for (let i = 0; i < this.selectedFiles.length; i++) {
      const currentFile = this.selectedFiles.item(i);
      if (!this.validateFile(currentFile.name)) {
        const msg = "File name " + currentFile.name + " do not match the expected format!";
        this.notifierService.notify("error", msg);
      } else {
        this.validatedFiles.push(currentFile);
        this.progressInfos.push({ value: 0, fileName: currentFile.name });
      }
    }
  }

  upload(): void {
    this.validatedFiles.forEach((file, index) => {
      this.filesService
        .uploadGlobal(file, this.selectedTestArea)
        .pipe(timeout(60000))
        .subscribe(
          (event) => {
            if (event.type === HttpEventType.UploadProgress) {
              this.progressInfos[index].value = Math.round((100 * event.loaded) / event.total);
            } else if (event instanceof HttpResponse) {
              this.notifierService.notify("success", event.body.message);
            }
          },
          (error) => {
            const errorMessage = ErrorHelper.disectError("Could not upload the file: " + file.name, error);
            this.progressInfos[index].value = 0;
            this.notifierService.notify("error", errorMessage);
          }
        );
    });
    this.selectedFiles = undefined;
  }

  validateFile(fileName: string): boolean {
    const validFilename = REGEXP_FILE_NAME.test(fileName);
    return validFilename;
  }
}

@Component({
  selector: "app-dialog-data-files-content",
  templateUrl: "dialog-data-files-content.component.html",
})
export class DialogDataFilesContentComponent {
  constructor(@Inject(MAT_DIALOG_DATA) public data: DialogData) {}
}

export interface DialogData {
  text: string;
}
